import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import PortalShell from "@/components/PortalShell";
import { Avatar, StatusPill, rupee } from "@/components/Bits";
import { toast } from "sonner";
import { Globe, Check } from "lucide-react";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { key: "carers", label: "Carers", icon: "UserRoundCheck" },
  { key: "seekers", label: "Care Seekers", icon: "Users" },
  { key: "bookings", label: "Bookings", icon: "CalendarDays" },
  { key: "settlements", label: "Settlements", icon: "Banknote" },
  { key: "reviews", label: "Reviews", icon: "Star" },
  { key: "masterdata", label: "Master Data", icon: "Database" },
];

export default function AdminPortal() {
  const [active, setActive] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [carers, setCarers] = useState([]);
  const [seekers, setSeekers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [meta, setMeta] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/carers").then((r) => setCarers(r.data)).catch(() => {});
    api.get("/admin/seekers").then((r) => setSeekers(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/admin/settlements").then((r) => setSettlements(r.data)).catch(() => {});
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const titles = { dashboard: "Dashboard", carers: "Carers", seekers: "Care Seekers", bookings: "Bookings", settlements: "Settlements", reviews: "Reviews", masterdata: "Master Data" };
  const pendingCarers = useMemo(() => carers.filter((c) => c.verification_status === "Pending"), [carers]);

  const verify = async (id) => { try { await api.put(`/admin/carers/${id}/verify`); toast.success("Carer verified · email sent"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const suspend = async (id) => { try { await api.put(`/admin/carers/${id}/status`, { status: "Suspended" }); toast("Carer suspended"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const generateSettlements = async () => { try { const { data } = await api.post("/admin/settlements/generate"); toast.success(`Generated ${data.generated} new settlement(s)`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const paySettlement = async (id) => { try { await api.post(`/admin/settlements/${id}/pay`); toast.success("Payout processed"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const payout = async () => { try { const { data } = await api.post("/admin/settlements/process-all"); toast.success(`Settled ${data.settled} carer payout(s)`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <PortalShell nav={NAV} active={active} setActive={setActive} title={titles[active]} accent="coral">
      {active === "dashboard" && stats && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <Kpi label="Active Carers" value={stats.total_carers} />
            <Kpi label="Verified" value={`${stats.verified_carers} (${stats.verified_pct}%)`} />
            <Kpi label="Care Seekers" value={stats.total_seekers.toLocaleString("en-IN")} />
            <Kpi label="Bookings (mo)" value={stats.bookings_month} />
            <Kpi label="Revenue (mo)" value={rupee(stats.revenue_month)} />
            <Kpi label="Pending Verif." value={stats.pending_verifications} accent />
          </div>
          <div className="card-bmc p-5">
            <h3 className="font-display font-bold mb-4">Pending verifications</h3>
            <div className="space-y-2">
              {pendingCarers.map((c) => (
                <div key={c.id} className="flex items-center gap-3 p-3 bg-ivory-2 rounded-r">
                  <Avatar initials={c.avatar_initials} gradient={c.gradient} size={38} />
                  <div className="flex-1"><p className="font-semibold text-sm">{c.name}</p><p className="text-xs text-ink-soft">{c.role} · {c.city}</p></div>
                  <button onClick={() => verify(c.id)} className="btn-pill bg-sage text-white px-4 py-2 text-xs" data-testid={`verify-${c.id}`}>Verify</button>
                </div>
              ))}
              {pendingCarers.length === 0 && <p className="text-sm text-ink-soft">No pending verifications.</p>}
            </div>
          </div>
        </div>
      )}

      {active === "carers" && (
        <Table head={["CG ID", "Name", "Category", "City", "Verification", "Eng.", "Rating", "Status", "Actions"]}>
          {carers.map((c) => (
            <tr key={c.id} className="border-b border-line-2" data-testid={`admin-carer-${c.id}`}>
              <td className="p-3 font-mono text-xs">{c.id}</td>
              <td className="p-3 flex items-center gap-2"><Avatar initials={c.avatar_initials} gradient={c.gradient} size={28} />{c.name}</td>
              <td className="p-3 text-ink-soft">{c.role}</td><td className="p-3">{c.city}</td>
              <td className="p-3"><StatusPill status={c.verification_status === "Verified" ? "Verified" : "Pending"} /></td>
              <td className="p-3">{c.engagements}</td><td className="p-3">{c.rating || "—"}</td>
              <td className="p-3"><StatusPill status={c.status} /></td>
              <td className="p-3"><div className="flex gap-2">
                {c.verification_status !== "Verified" && <button onClick={() => verify(c.id)} className="text-xs text-sage-deep font-semibold">Verify</button>}
                {c.status === "Active" && <button onClick={() => suspend(c.id)} className="text-xs text-coral-deep">Suspend</button>}
              </div></td>
            </tr>
          ))}
        </Table>
      )}

      {active === "seekers" && (
        <Table head={["Name", "Email", "City", "NRI", "Recipients", "Bookings", "Status"]}>
          {seekers.map((s) => (
            <tr key={s.id} className="border-b border-line-2">
              <td className="p-3 font-medium">{s.name}</td><td className="p-3 text-ink-soft">{s.email}</td><td className="p-3">{s.city}</td>
              <td className="p-3">{s.is_nri ? <Globe size={16} className="text-coral" /> : "—"}</td>
              <td className="p-3">{s.recipients}</td><td className="p-3">{s.bookings}</td><td className="p-3"><StatusPill status="Active" /></td>
            </tr>
          ))}
        </Table>
      )}

      {active === "bookings" && (
        <Table head={["BK ID", "Seeker", "Carer", "Service", "Date", "Amount", "Channel", "Payment", "Status"]}>
          {bookings.map((b) => (
            <tr key={b.id} className="border-b border-line-2">
              <td className="p-3 font-mono text-xs">{b.id}</td><td className="p-3">{b.seeker_name}</td><td className="p-3">{b.carer_name}</td>
              <td className="p-3 text-ink-soft">{b.service_type}</td><td className="p-3 text-ink-soft">{b.date}</td><td className="p-3 font-semibold">{rupee(b.amount)}</td>
              <td className="p-3 text-xs">{b.channel}</td><td className="p-3"><StatusPill status={b.payment_status} /></td><td className="p-3"><StatusPill status={b.status} /></td>
            </tr>
          ))}
        </Table>
      )}

      {active === "settlements" && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <p className="text-sm text-ink-soft"><b className="text-ink">{settlements.filter((s) => s.status === "Unsettled").length}</b> pending · Net due {rupee(settlements.filter((s) => s.status === "Unsettled").reduce((s, x) => s + x.net, 0))} <span className="text-ink-faint">· auto-runs every Monday</span></p>
            <div className="flex gap-2">
              <button onClick={generateSettlements} className="btn-pill border border-line px-4 py-2.5 text-sm hover:border-coral" data-testid="generate-settlements">Generate this week</button>
              <button onClick={payout} className="btn-pill bg-coral text-white px-5 py-2.5 text-sm" data-testid="process-payouts">Process all pending</button>
            </div>
          </div>
          <Table head={["STL ID", "Carer", "Period", "Jobs", "Gross", "Comm. (8%)", "TDS (2%)", "Net Payout", "Bank", "Status", ""]}>
            {settlements.map((s) => (
              <tr key={s.id} className="border-b border-line-2" data-testid={`settlement-${s.id}`}>
                <td className="p-3 font-mono text-xs">{s.id}</td><td className="p-3">{s.carer_name}</td><td className="p-3 text-ink-soft text-xs">{s.period}</td>
                <td className="p-3 text-ink-soft">{s.bookings_count ?? "-"}</td>
                <td className="p-3">{rupee(s.gross)}</td><td className="p-3 text-ink-soft">{rupee(s.commission)}</td><td className="p-3 text-ink-soft">{rupee(s.tds)}</td>
                <td className="p-3 font-semibold">{rupee(s.net)}</td><td className="p-3 text-xs">{s.bank}</td><td className="p-3"><StatusPill status={s.status} /></td>
                <td className="p-3">{s.status === "Unsettled" && <button onClick={() => paySettlement(s.id)} className="text-xs text-coral font-semibold" data-testid={`pay-settlement-${s.id}`}>Pay</button>}</td>
              </tr>
            ))}
            {settlements.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-ink-soft text-sm">No settlements yet — click "Generate this week".</td></tr>}
          </Table>
        </div>
      )}

      {active === "reviews" && (
        <Table head={["Date", "Carer", "Reviewer", "Rating", "Review", "Status"]}>
          {reviews.map((r) => (
            <tr key={r.id} className="border-b border-line-2">
              <td className="p-3 text-ink-soft">{r.date}</td>
              <td className="p-3">{carers.find((c) => c.id === r.carer_id)?.name || r.carer_id}</td>
              <td className="p-3">{r.reviewer_name}</td><td className="p-3 text-butter">{"★".repeat(r.rating)}</td>
              <td className="p-3 text-ink-soft max-w-xs truncate">{r.text}</td><td className="p-3"><StatusPill status={r.status === "Approved" ? "Verified" : "Pending"} /></td>
            </tr>
          ))}
        </Table>
      )}

      {active === "masterdata" && meta && (
        <div className="space-y-6">
          {meta.categories.map((cat) => (
            <div key={cat.key} className="card-bmc p-5">
              <h3 className="font-display font-bold mb-3">{cat.label}</h3>
              <div className="flex flex-wrap gap-2">
                {(meta.services_by_category[cat.key] || []).map((s) => (
                  <span key={s} className="text-xs bg-ivory-2 px-3 py-1.5 rounded-full flex items-center gap-1.5"><Check size={12} className="text-sage" /> {s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </PortalShell>
  );
}

function Kpi({ label, value, accent }) {
  return <div className={`card-bmc p-5 ${accent ? "ring-1 ring-coral" : ""}`}><p className="text-sm text-ink-soft">{label}</p><p className="font-display font-bold text-2xl mt-1">{value}</p></div>;
}

function Table({ head, children }) {
  return (
    <div className="card-bmc p-2 overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-ink-soft bg-ivory-2">{head.map((h) => <th key={h} className="p-3 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
